import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  Brain,
  Clock,
  DollarSign,
  Eye,
  Target,
  Users,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { showToastError, showToastSuccess } from "../../shared/alertService";
import apiService from "../../shared/apiService";
import { API_ENDPOINTS } from "../../shared/constants";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import Card, {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/Card";
import Progress from "../ui/Progress";
import Select from "../ui/Select";

interface AttributionData {
  summary: {
    total_leads: number;
    total_conversions: number;
    overall_conversion_rate: number;
    organic_share: number;
    paid_share: number;
    organic_conversion_rate: number;
    paid_conversion_rate: number;
  };
  organic_performance: {
    total_organic_leads: number;
    total_organic_conversions: number;
    organic_conversion_rate: number;
    source_analysis: { [key: string]: any };
    top_keywords: [string, number][];
    journey_metrics: {
      avg_journey_time_days: number;
      avg_touchpoints: number;
      avg_quality_score: number;
    };
    optimization_opportunities: string[];
  };
  paid_performance: {
    total_paid_leads: number;
    total_paid_conversions: number;
    paid_conversion_rate: number;
    source_analysis: { [key: string]: any };
    campaign_analysis: { [key: string]: any };
    top_keywords: [string, number][];
    optimization_opportunities: string[];
  };
  optimization_strategy: {
    organic_to_paid_strategy: any;
    recommended_actions: string[];
  };
  roi_projections: {
    projected_organic_leads: number;
    projected_paid_leads: number;
    projected_total_conversions: number;
    projected_cost: number;
    projected_revenue: number;
    projected_roi: number;
    payback_period_months: number;
    break_even_point: number;
  };
}

interface TrafficIntelligenceData {
  performance_summary: { [key: string]: any };
  active_signals: any[];
  active_recommendations: any[];
  recent_automated_actions: any[];
  optimization_rules: any[];
  monitoring_status: {
    is_active: boolean;
    last_update: string;
    signals_detected: number;
    recommendations_generated: number;
    actions_executed: number;
  };
}

const LeadAttributionDashboard: React.FC = () => {
  const [attributionData, setAttributionData] =
    useState<AttributionData | null>(null);
  const [trafficIntelligenceData, setTrafficIntelligenceData] =
    useState<TrafficIntelligenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30d");
  const [selectedSource, setSelectedSource] = useState("all");

  useEffect(() => {
    fetchAttributionData();
    fetchTrafficIntelligenceData();
  }, [timeRange, selectedSource]);

  const fetchAttributionData = async () => {
    try {
      const data = await apiService.get(API_ENDPOINTS.ATTRIBUTION.REPORT);
      setAttributionData(data);
    } catch (error) {
      console.error("Error fetching attribution data:", error);
      showToastError("Erro ao carregar dados de attribution");
    }
  };

  const fetchTrafficIntelligenceData = async () => {
    try {
      const data = await apiService.get(
        API_ENDPOINTS.TRAFFIC_INTELLIGENCE.REPORT,
      );
      setTrafficIntelligenceData(data);
    } catch (error) {
      console.error("Error fetching traffic intelligence data:", error);
      showToastError("Erro ao carregar dados de inteligência de tráfego");
    } finally {
      setLoading(false);
    }
  };

  const generateOptimizationStrategy = async () => {
    try {
      setLoading(true);
      await apiService.post(API_ENDPOINTS.ATTRIBUTION.GENERATE_STRATEGY);

      showToastSuccess("Estratégia de otimização gerada com sucesso!");
      fetchAttributionData();
    } catch (error) {
      console.error("Error generating strategy:", error);
      showToastError("Erro ao gerar estratégia de otimização");
    } finally {
      setLoading(false);
    }
  };

  const executeOptimizationAction = async (actionId: string) => {
    try {
      await apiService.post(
        API_ENDPOINTS.TRAFFIC_INTELLIGENCE.EXECUTE_ACTION.replace(
          "{id}",
          actionId,
        ),
      );

      showToastSuccess("Ação de otimização executada!");
      fetchTrafficIntelligenceData();
    } catch (error) {
      console.error("Error executing action:", error);
      showToastError("Erro ao executar ação de otimização");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <Brain className="h-8 w-8 mr-3 text-purple-600" />
          Dashboard de Attribution de Leads
        </h1>
        <p className="text-gray-600">
          Análise inteligente de leads orgânicos vs pagos com otimização
          automática
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex space-x-4">
        <div className="w-48">
          <Select
            value={timeRange}
            onChange={setTimeRange}
            options={[
              { value: "7d", label: "Últimos 7 dias" },
              { value: "30d", label: "Últimos 30 dias" },
              { value: "90d", label: "Últimos 90 dias" },
            ]}
            placeholder="Período"
          />
        </div>

        <div className="w-48">
          <Select
            value={selectedSource}
            onChange={setSelectedSource}
            options={[
              { value: "all", label: "Todas as fontes" },
              { value: "organic", label: "Apenas orgânico" },
              { value: "paid", label: "Apenas pago" },
            ]}
            placeholder="Fonte"
          />
        </div>

        <Button onClick={generateOptimizationStrategy} className="ml-auto">
          <Zap className="h-4 w-4 mr-2" />
          Gerar Estratégia de Otimização
        </Button>
      </div>

      {/* Resumo Executivo */}
      {attributionData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total de Leads
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {attributionData.summary.total_leads.toLocaleString()}
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Orgânico:{" "}
                  {(attributionData.summary.organic_share * 100).toFixed(1)}%
                </div>
                <div className="flex items-center ml-4">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  Pago: {(attributionData.summary.paid_share * 100).toFixed(1)}%
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Taxa de Conversão
              </CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(
                  attributionData.summary.overall_conversion_rate * 100
                ).toFixed(2)}
                %
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Orgânico:{" "}
                  {(
                    attributionData.summary.organic_conversion_rate * 100
                  ).toFixed(2)}
                  %
                </div>
                <div className="flex items-center ml-4">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  Pago:{" "}
                  {(attributionData.summary.paid_conversion_rate * 100).toFixed(
                    2,
                  )}
                  %
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                ROI Projetado
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(attributionData.roi_projections.projected_roi * 100).toFixed(
                  0,
                )}
                %
              </div>
              <div className="text-xs text-muted-foreground">
                Receita: R${" "}
                {attributionData.roi_projections.projected_revenue.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Payback Period
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {attributionData.roi_projections.payback_period_months} meses
              </div>
              <div className="text-xs text-muted-foreground">
                Break-even:{" "}
                {attributionData.roi_projections.break_even_point.toFixed(0)}{" "}
                conversões
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance por Fonte */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Performance Orgânica */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              Performance Orgânica
            </CardTitle>
            <CardDescription>
              Análise de tráfego orgânico e oportunidades
            </CardDescription>
          </CardHeader>
          <CardContent>
            {attributionData?.organic_performance && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Total de Leads</div>
                    <div className="text-2xl font-bold">
                      {attributionData.organic_performance.total_organic_leads}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Conversões</div>
                    <div className="text-2xl font-bold">
                      {
                        attributionData.organic_performance
                          .total_organic_conversions
                      }
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    Taxa de Conversão
                  </div>
                  <Progress
                    value={
                      attributionData.organic_performance
                        .organic_conversion_rate * 100
                    }
                    className="h-2"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(
                      attributionData.organic_performance
                        .organic_conversion_rate * 100
                    ).toFixed(2)}
                    %
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    Top Keywords Orgânicos
                  </div>
                  <div className="space-y-1">
                    {attributionData.organic_performance.top_keywords
                      .slice(0, 5)
                      .map(([keyword, count], index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center text-sm"
                        >
                          <span className="truncate">{keyword}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    Métricas de Jornada
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-semibold">
                        {attributionData.organic_performance.journey_metrics.avg_journey_time_days.toFixed(
                          1,
                        )}
                        d
                      </div>
                      <div className="text-gray-500">Tempo médio</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold">
                        {attributionData.organic_performance.journey_metrics.avg_touchpoints.toFixed(
                          1,
                        )}
                      </div>
                      <div className="text-gray-500">Touchpoints</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold">
                        {(
                          attributionData.organic_performance.journey_metrics
                            .avg_quality_score * 100
                        ).toFixed(0)}
                        %
                      </div>
                      <div className="text-gray-500">Qualidade</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance Paga */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              Performance Paga
            </CardTitle>
            <CardDescription>
              Análise de campanhas pagas e otimizações
            </CardDescription>
          </CardHeader>
          <CardContent>
            {attributionData?.paid_performance && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Total de Leads</div>
                    <div className="text-2xl font-bold">
                      {attributionData.paid_performance.total_paid_leads}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Conversões</div>
                    <div className="text-2xl font-bold">
                      {attributionData.paid_performance.total_paid_conversions}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    Taxa de Conversão
                  </div>
                  <Progress
                    value={
                      attributionData.paid_performance.paid_conversion_rate *
                      100
                    }
                    className="h-2"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(
                      attributionData.paid_performance.paid_conversion_rate *
                      100
                    ).toFixed(2)}
                    %
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    Top Keywords Pagos
                  </div>
                  <div className="space-y-1">
                    {attributionData.paid_performance.top_keywords
                      .slice(0, 5)
                      .map(([keyword, count], index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center text-sm"
                        >
                          <span className="truncate">{keyword}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-2">
                    Análise por Fonte
                  </div>
                  <div className="space-y-1">
                    {Object.entries(
                      attributionData.paid_performance.source_analysis,
                    )
                      .slice(0, 3)
                      .map(([source, data]: [string, any]) => (
                        <div
                          key={source}
                          className="flex justify-between items-center text-xs"
                        >
                          <span className="capitalize">
                            {source.replace("_", " ")}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span>{data.leads} leads</span>
                            <Badge variant="outline">
                              {(data.conversion_rate * 100).toFixed(1)}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inteligência de Tráfego */}
      {trafficIntelligenceData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Sinais Ativos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="h-5 w-5 mr-2 text-orange-500" />
                Sinais Ativos
              </CardTitle>
              <CardDescription>Sinais de otimização detectados</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {trafficIntelligenceData.active_signals
                  .slice(0, 5)
                  .map((signal, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-purple-50 rounded-sm border border-purple-300"
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-2 h-2 rounded-full mr-2 ${
                            signal.priority === "critical"
                              ? "bg-red-500"
                              : signal.priority === "high"
                                ? "bg-orange-500"
                                : signal.priority === "medium"
                                  ? "bg-yellow-500"
                                  : "bg-green-500"
                          }`}
                        ></div>
                        <div>
                          <div className="text-sm font-medium">
                            {signal.metric}
                          </div>
                          <div className="text-xs text-gray-500">
                            {signal.source}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline">{signal.priority}</Badge>
                    </div>
                  ))}
                {trafficIntelligenceData.active_signals.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    Nenhum sinal ativo
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recomendações */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Brain className="h-5 w-5 mr-2 text-purple-500" />
                Recomendações
              </CardTitle>
              <CardDescription>Sugestões de otimização</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {trafficIntelligenceData.active_recommendations
                  .slice(0, 5)
                  .map((rec, index) => (
                    <div
                      key={index}
                      className="p-3 bg-purple-50 rounded-sm border border-purple-300"
                    >
                      <div className="text-sm font-medium mb-1">
                        {rec.title}
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        {rec.description}
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{rec.priority}</Badge>
                        <div className="text-xs text-gray-500">
                          {(rec.confidence * 100).toFixed(0)}% confiança
                        </div>
                      </div>
                    </div>
                  ))}
                {trafficIntelligenceData.active_recommendations.length ===
                  0 && (
                  <div className="text-center text-gray-500 py-4">
                    Nenhuma recomendação ativa
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Ações Automatizadas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Zap className="h-5 w-5 mr-2 text-yellow-500" />
                Ações Automatizadas
              </CardTitle>
              <CardDescription>Últimas ações executadas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {trafficIntelligenceData.recent_automated_actions
                  .slice(0, 5)
                  .map((action, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-purple-50 rounded-sm border border-purple-300"
                    >
                      <div>
                        <div className="text-sm font-medium">
                          {action.rule_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {action.action_type}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">{action.priority}</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            executeOptimizationAction(action.action_id)
                          }
                        >
                          Executar
                        </Button>
                      </div>
                    </div>
                  ))}
                {trafficIntelligenceData.recent_automated_actions.length ===
                  0 && (
                  <div className="text-center text-gray-500 py-4">
                    Nenhuma ação recente
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Estratégia de Otimização */}
      {attributionData?.optimization_strategy && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2 text-green-500" />
              Estratégia de Otimização Orgânico → Pago
            </CardTitle>
            <CardDescription>Recomendações para maximizar ROI</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Ações Recomendadas</h4>
                <div className="space-y-2">
                  {attributionData.optimization_strategy.recommended_actions.map(
                    (action, index) => (
                      <div key={index} className="flex items-start">
                        <ArrowUpRight className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                        <span className="text-sm">{action}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">
                  Oportunidades de Otimização
                </h4>
                <div className="space-y-2">
                  {attributionData.organic_performance.optimization_opportunities
                    .slice(0, 3)
                    .map((opportunity, index) => (
                      <div key={index} className="flex items-start">
                        <ArrowDownRight className="h-4 w-4 text-orange-500 mr-2 mt-0.5" />
                        <span className="text-sm">{opportunity}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status do Monitoramento */}
      {trafficIntelligenceData?.monitoring_status && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Eye className="h-5 w-5 mr-2 text-blue-500" />
              Status do Monitoramento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">
                  {trafficIntelligenceData.monitoring_status.is_active
                    ? "Ativo"
                    : "Inativo"}
                </div>
                <div className="text-sm text-gray-600">Sistema</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {trafficIntelligenceData.monitoring_status.signals_detected}
                </div>
                <div className="text-sm text-gray-600">Sinais</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {
                    trafficIntelligenceData.monitoring_status
                      .recommendations_generated
                  }
                </div>
                <div className="text-sm text-gray-600">Recomendações</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {trafficIntelligenceData.monitoring_status.actions_executed}
                </div>
                <div className="text-sm text-gray-600">Ações</div>
              </div>
            </div>
            <div className="mt-4 text-center text-sm text-gray-500">
              Última atualização:{" "}
              {new Date(
                trafficIntelligenceData.monitoring_status.last_update,
              ).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadAttributionDashboard;
